// The refund breakdown of a return/refund step preview in the admin agent
// column (2026-10-23, docs/agent-draft-shadow.md, Return/Refund previews):
// Original Refund, Return Label Deduction and Final Refund, from the same
// math the main panel and the server use (estimatedRefundBreakdown() and
// computeLineTax() in refund-amounts.js). Pure, shared by the server (the
// preview's defaults, the agent's calculate_refund tool) and the panel (live
// as an admin edits the items, the reason or the shipping option). Display
// only: nothing here runs or sends anything.

import { computeLineTax, estimatedRefundBreakdown } from './refund-amounts.js'

// The main panel's three "Return shipping — who pays?" choices when the
// customer pays (SuggestionPanel.jsx; action-executor.js's
// shippingPaymentOption): the customer's cost (the ESTIMATED rate), our cost
// (the NEGOTIATED rate), or the customer ships it with their own label.
export const RETURN_SHIPPING_OPTIONS = Object.freeze([
  Object.freeze({ value: 'customerCost', label: 'Customer Cost' }),
  Object.freeze({ value: 'ourCost', label: 'Our Cost' }),
  Object.freeze({ value: 'customerShipsOwn', label: 'Customer pays for their own shipping' }),
])
export const RETURN_SHIPPING_OPTION_VALUES = Object.freeze(RETURN_SHIPPING_OPTIONS.map((o) => o.value))
export const DISPOSITION_VALUES = Object.freeze(['return', 'scrap'])

const round2 = (n) => Math.round(n * 100) / 100
const num = (v) => {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}
const sameItems = (a = [], b = []) => {
  const key = (list) => (list ?? []).map((i) => `${String(i?.sku ?? '').toUpperCase()}x${Number(i?.quantity) || 1}`).sort().join(',')
  return key(a) === key(b)
}

// Original Refund for the selected items: their items plus tax, null when the
// order's tax or total is missing or a selected line can't be priced (never a
// guess; the same rule as refund-amount-fill.js's originalRefundFromOrder()).
export function originalRefundFor({ allItems, selectedItems, orderTax, shippingCost = null, orderTotal }) {
  const tax = num(orderTax)
  const total = num(orderTotal)
  if (!Array.isArray(allItems) || !allItems.length || !Array.isArray(selectedItems) || !selectedItems.length) return null
  if (tax == null || total == null) return null
  const lineTaxes = computeLineTax(allItems, { orderTax: tax, shippingCost: num(shippingCost), orderTotal: total })
  const picked = allItems.map((it, idx) => (selectedItems.some((s) => s.sku === it.sku) ? idx : -1)).filter((i) => i >= 0)
  if (picked.length !== new Set(selectedItems.map((s) => s.sku)).size) return null
  if (picked.some((i) => lineTaxes[i] == null)) return null
  const { itemsTotal, unresolvedSkus } = estimatedRefundBreakdown({ allItems, selectedItems, lineTaxes, deduction: 0 })
  if (unresolvedSkus.length || !Number.isFinite(itemsTotal)) return null
  return round2(itemsTotal)
}

// Who pays return shipping for a reason code, from the preview's options
// ({ value, payer }); null when unknown.
export function payerForReason(reasonCodes, reasonCode) {
  return (reasonCodes ?? []).find((o) => String(o?.value) === String(reasonCode ?? ''))?.payer ?? null
}

// { original, deduction, final, deductionSource } for one preview's current
// values. refund: the preview's refund facts ({ allItems, orderTax,
// shippingCost, orderTotal, rateQuote: { items, totalEstimated,
// totalNegotiated } | null, rmaAdjustment }); values: { items, reasonCode,
// disposition, shippingPaymentOption, labelDeduction }; payer: 'company' |
// 'customer' | null. The deduction:
//   company pays, or the part is scrapped (no label)   0
//   the customer ships it with their own label          0
//   an amount a person typed (labelDeduction)           that amount
//   the RMA's own label charge (its adjustment)         that amount
//   the Check rate quote for exactly these items        the option's rate
//   otherwise                                           null: unknown, left
//                                                       for a person
// Final = Original − Deduction, null when either is unknown.
export function returnRefundBreakdown({ refund = null, values = {}, payer = null } = {}) {
  const r = refund ?? {}
  const v = values ?? {}
  const original = originalRefundFor({ allItems: r.allItems, selectedItems: v.items ?? [], orderTax: r.orderTax, shippingCost: r.shippingCost, orderTotal: r.orderTotal })
  let deduction = null
  let deductionSource = null
  const typed = num(v.labelDeduction)
  if (payer === 'company') { deduction = 0; deductionSource = 'company' } else if (v.disposition === 'scrap') { deduction = 0; deductionSource = 'scrap' } else if (payer === 'customer' && v.shippingPaymentOption === 'customerShipsOwn') { deduction = 0; deductionSource = 'customer_ships_own' } else if (typed != null && typed >= 0) { deduction = round2(typed); deductionSource = 'entered' } else if (payer === 'customer' && num(r.rmaAdjustment) != null) { deduction = round2(num(r.rmaAdjustment)); deductionSource = 'rma_note' } else if (payer === 'customer' && r.rateQuote && sameItems(r.rateQuote.items, v.items)) {
    const option = v.shippingPaymentOption || 'customerCost'
    const rate = option === 'ourCost' ? num(r.rateQuote.totalNegotiated) : num(r.rateQuote.totalEstimated)
    if (rate != null) { deduction = round2(rate); deductionSource = option === 'ourCost' ? 'rate_quote_our_cost' : 'rate_quote_customer_cost' }
  }
  const final = original != null && deduction != null ? round2(original - deduction) : null
  return { original, deduction, final, deductionSource }
}

// Where a deduction came from, in words for the panel.
export const DEDUCTION_SOURCE_LABELS = Object.freeze({
  company: 'company pays return shipping',
  scrap: 'no label: the part is scrapped',
  customer_ships_own: 'the customer ships it with their own label',
  entered: 'entered by a person',
  rma_note: "the RMA's label charge",
  rate_quote_customer_cost: 'rate quote, Customer Cost',
  rate_quote_our_cost: 'rate quote, Our Cost',
})
